import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { searchAPI } from "../../services/api";
import { getImageUrl } from "../../utils/image";

const aboutSubmenu = [
  { label: "About Us", path: "/about", icon: "Info" },
  { label: "Our Services", path: "/services", icon: "Wrench" },
  { label: "Gallery", path: "/gallery", icon: "Image" },
];

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about", hasDropdown: true, submenu: aboutSubmenu },
  { label: "Products", path: "/products" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopDropOpen, setDesktopDropOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const dropRef = useRef(null);
  const searchInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { get } = useSettings();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDesktopDropOpen(false);
    setSearchModalOpen(false);
  }, [location]);

  useEffect(() => {
    const fn = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDesktopDropOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // Keyboard shortcut Ctrl+K or / to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchModalOpen(true);
      }
      if (e.key === "Escape") {
        setSearchModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (searchModalOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchQuery("");
      setSearchResults(null);
    }
  }, [searchModalOpen]);

  // Live search debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(() => {
      searchAPI.search(searchQuery.trim())
        .then(r => setSearchResults(r.data))
        .catch(() => setSearchResults(null))
        .finally(() => setSearchLoading(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isActive = (path) => path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  // Dynamic menu list parsing
  let linksList = navLinks;
  const rawMenu = get("navigation_menu_json");
  if (rawMenu) {
    try {
      linksList = JSON.parse(rawMenu);
    } catch (e) {
      console.error("Invalid navigation_menu_json", e);
    }
  }

  const logoUrl = getImageUrl(get("site_logo") || "/logo.png");
  const headerCtaText = get("header_cta_text") || "Get in Touch";
  const headerCtaLink = get("header_cta_link") || "/contact";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-soft border-b border-gray-100 transition-all duration-300 ${scrolled ? "py-1" : "py-0"}`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-0">
              <img src={logoUrl} alt={get("site_name") || "Sava Fresh"} className="h-10 md:h-12 w-auto object-contain" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {linksList.map((link) =>
                link.hasDropdown ? (
                  <div key={link.path} className="relative" ref={dropRef}>
                    <button
                      onClick={() => setDesktopDropOpen(!desktopDropOpen)}
                      className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(link.path) ? "text-brand-primary bg-brand-pale" : "text-gray-600 hover:text-brand-primary hover:bg-gray-50"}`}
                    >
                      {link.label}
                      <LucideIcons.ChevronDown className={`w-3.5 h-3.5 transition-transform ${desktopDropOpen ? "rotate-180" : ""}`} />
                    </button>
                    {desktopDropOpen && (
                      <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-hover border border-gray-100 overflow-hidden z-50">
                        <div className="p-2">
                          {link.submenu?.map((sub) => {
                            const IconComponent = LucideIcons[sub.icon] || LucideIcons.HelpCircle;
                            return (
                              <Link key={sub.path} to={sub.path} onClick={() => setDesktopDropOpen(false)}
                                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-brand-pale transition-colors group">
                                <div className="w-8 h-8 bg-brand-pale rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-brand-muted transition-colors">
                                  <IconComponent className="w-4 h-4 text-brand-primary" />
                                </div>
                                <span className="text-sm font-medium text-gray-700 group-hover:text-brand-primary">{sub.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link key={link.path} to={link.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(link.path) ? "text-brand-primary bg-brand-pale" : "text-gray-600 hover:text-brand-primary hover:bg-gray-50"}`}>
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Actions: Search + Call + CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={() => setSearchModalOpen(true)}
                className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-brand-pale text-gray-500 hover:text-brand-primary flex items-center justify-center transition-all border border-gray-100"
                title="Search (Ctrl + K)"
                aria-label="Search"
              >
                <LucideIcons.Search className="w-4 h-4" />
              </button>

              <a href={`tel:${get("call_number")}`} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-brand-primary transition-colors">
                <LucideIcons.Phone className="w-4 h-4" />
                <span className="hidden xl:block">{get("call_number")}</span>
              </a>
              <Link to={headerCtaLink} className="btn-primary text-xs px-5 py-2.5">{headerCtaText}</Link>
            </div>

            {/* Mobile Actions: Search + Menu */}
            <div className="flex lg:hidden items-center gap-2">
              <button
                onClick={() => setSearchModalOpen(true)}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-600"
                aria-label="Search"
              >
                <LucideIcons.Search className="w-5 h-5" />
              </button>
              <button onClick={() => setMenuOpen(!menuOpen)} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
                {menuOpen ? <LucideIcons.X className="w-5 h-5 text-gray-700" /> : <LucideIcons.Menu className="w-5 h-5 text-gray-700" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}`}>
          <div className="bg-white border-t border-gray-100 px-4 py-4 shadow-soft">
            <nav className="flex flex-col gap-1">
              {linksList.map((link) => (
                link.hasDropdown ? (
                  <div key={link.path} className="space-y-1">
                    <span className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider block">{link.label}</span>
                    <div className="pl-2 space-y-1">
                      {link.submenu?.map((sub) => {
                        const IconComponent = LucideIcons[sub.icon] || LucideIcons.HelpCircle;
                        return (
                          <Link key={sub.path} to={sub.path} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-brand-pale hover:text-brand-primary">
                            <IconComponent className="w-4 h-4 text-brand-primary" />
                            {sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <Link key={link.path} to={link.path} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${isActive(link.path) ? "text-brand-primary bg-brand-pale" : "text-gray-700 hover:bg-gray-50"}`}>
                    {link.label}
                  </Link>
                )
              ))}
              <div className="pt-4 mt-2 border-t border-gray-100 flex flex-col gap-3">
                <a href={`tel:${get("call_number")}`} className="flex items-center gap-2 text-sm text-gray-700 px-3">
                  <LucideIcons.Phone className="w-4 h-4 text-brand-primary" />
                  {get("call_number")}
                </a>
                <Link to={headerCtaLink} className="btn-primary justify-center text-xs py-3">{headerCtaText}</Link>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* ── GLOBAL SEARCH MODAL ───────────────────────── */}
      {searchModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 pt-20 animate-fade-in" onClick={() => setSearchModalOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-gray-100" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <LucideIcons.Search className="w-5 h-5 text-brand-primary flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search products, categories, blogs..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-transparent border-0 focus:outline-none text-base text-gray-800 placeholder-gray-400"
              />
              {searchLoading && <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />}
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-gray-600 p-1">
                  <LucideIcons.X className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => setSearchModalOpen(false)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2.5 py-1 rounded-lg">
                ESC
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-5 space-y-6">
              {!searchQuery.trim() ? (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">Type a keyword to search products, categories, and articles.</p>
                  <div className="flex justify-center gap-2 mt-4 flex-wrap">
                    {["Rice", "Milk", "Sugar", "Oil", "Trading"].map(k => (
                      <button
                        key={k}
                        onClick={() => setSearchQuery(k)}
                        className="px-3 py-1 bg-gray-50 hover:bg-brand-pale text-gray-600 hover:text-brand-primary text-xs rounded-full border border-gray-100 transition-colors"
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>
              ) : searchResults?.total === 0 && !searchLoading ? (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-base font-semibold text-gray-600">No results found for "{searchQuery}"</p>
                  <p className="text-xs mt-1">Try another keyword or search terms.</p>
                </div>
              ) : (
                <>
                  {/* Products */}
                  {searchResults?.products?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center justify-between">
                        <span>Products</span>
                        <span className="text-brand-primary">{searchResults.products.length} found</span>
                      </h4>
                      <div className="space-y-2">
                        {searchResults.products.map(p => (
                          <Link
                            key={p.id}
                            to={`/products/${p.slug}`}
                            onClick={() => setSearchModalOpen(false)}
                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-brand-pale transition-colors group"
                          >
                            <div className="w-12 h-12 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0 border border-gray-100">
                              {p.imageUrl ? (
                                <img src={getImageUrl(p.imageUrl)} alt={p.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-brand-primary text-xs">📦</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 group-hover:text-brand-primary transition-colors truncate">
                                {p.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{p.category} {p.price ? `• ${p.price}` : ""}</p>
                            </div>
                            <LucideIcons.ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-brand-primary group-hover:translate-x-1 transition-all" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Categories */}
                  {searchResults?.categories?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        {searchResults.categories.map(c => (
                          <Link
                            key={c}
                            to={`/products?category=${encodeURIComponent(c)}`}
                            onClick={() => setSearchModalOpen(false)}
                            className="px-3 py-1.5 bg-gray-50 hover:bg-brand-pale text-gray-700 hover:text-brand-primary text-xs font-medium rounded-xl border border-gray-100 transition-colors"
                          >
                            🏷️ {c}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Blogs */}
                  {searchResults?.blogs?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center justify-between">
                        <span>Articles & Blog</span>
                        <span className="text-brand-primary">{searchResults.blogs.length} found</span>
                      </h4>
                      <div className="space-y-2">
                        {searchResults.blogs.map(b => (
                          <Link
                            key={b.id}
                            to={`/blog/${b.slug}`}
                            onClick={() => setSearchModalOpen(false)}
                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-brand-pale transition-colors group"
                          >
                            <div className="w-12 h-12 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0 border border-gray-100">
                              {b.imageUrl ? (
                                <img src={getImageUrl(b.imageUrl)} alt={b.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-brand-primary text-xs">📰</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 group-hover:text-brand-primary transition-colors truncate">
                                {b.title}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{b.category || "Article"}</p>
                            </div>
                            <LucideIcons.ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-brand-primary group-hover:translate-x-1 transition-all" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
